import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { auth } from '../Firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Swal from 'sweetalert2';

const AdminRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;

    const INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes in ms

    // Immediate check on mount/auth state load
    const lastActive = parseInt(localStorage.getItem('admin_last_active') || '0', 10);
    const now = Date.now();
    if (lastActive && (now - lastActive > INACTIVITY_LIMIT)) {
      signOut(auth).then(() => {
        localStorage.removeItem('admin_last_active');
        localStorage.removeItem('admin_last_active_write');
        Swal.fire({
          title: 'Session Expired',
          text: 'You have been logged out due to 5 minutes of inactivity.',
          icon: 'warning',
          confirmButtonColor: '#0B5D65',
          confirmButtonText: 'Login Again'
        });
      });
      return;
    }

    // Initialize timestamp if not present
    if (!localStorage.getItem('admin_last_active')) {
      localStorage.setItem('admin_last_active', now.toString());
      localStorage.setItem('admin_last_active_write', now.toString());
    }

    // Setup activity listeners
    const handleActivity = () => {
      const currentNow = Date.now();
      const lastWrite = parseInt(localStorage.getItem('admin_last_active_write') || '0', 10);
      
      // Throttle writes to localStorage to optimize performance (every 2 seconds)
      if (currentNow - lastWrite > 2000) {
        localStorage.setItem('admin_last_active', currentNow.toString());
        localStorage.setItem('admin_last_active_write', currentNow.toString());
      }
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    // Setup periodic checker
    const checkInactivity = setInterval(() => {
      const currentLastActive = parseInt(localStorage.getItem('admin_last_active') || '0', 10);
      const currentNow = Date.now();

      if (currentLastActive && (currentNow - currentLastActive > INACTIVITY_LIMIT)) {
        clearInterval(checkInactivity);
        signOut(auth).then(() => {
          localStorage.removeItem('admin_last_active');
          localStorage.removeItem('admin_last_active_write');
          Swal.fire({
            title: 'Session Expired',
            text: 'You have been logged out due to 5 minutes of inactivity.',
            icon: 'warning',
            confirmButtonColor: '#0B5D65',
            confirmButtonText: 'Login Again'
          });
        });
      }
    }, 5000); // Check every 5 seconds

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearInterval(checkInactivity);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-primary font-bold tracking-widest uppercase text-xs animate-pulse">Verifying Security...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" />;
  }

  return children;
};

export default AdminRoute;
